import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Lock, ShieldCheck, UserCog, Users } from 'lucide-react'
import { SuperAdminsTab } from '../components/tabs/SuperAdminsTab'
import { RolesTab } from '../components/tabs/RolesTab'
import { PermissionsTab } from '../components/tabs/PermissionsTab'
import { AllUsersTab } from '../components/tabs/AllUsersTab'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

type AdminTabId = 'users' | 'roles' | 'permissions' | 'all-users'

const VALID_TABS: AdminTabId[] = ['users', 'roles', 'permissions', 'all-users']

function isValidTab(v: string | null): v is AdminTabId {
  return VALID_TABS.includes(v as AdminTabId)
}

export function AdminPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const requestedTab = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState<AdminTabId>(
    isValidTab(requestedTab) ? requestedTab : 'users',
  )

  useEffect(() => {
    const tab = searchParams.get('tab')
    if (isValidTab(tab)) {
      setActiveTab(tab)
    }
  }, [searchParams])

  function handleTabChange(next: AdminTabId) {
    setActiveTab(next)
    const params = new URLSearchParams(searchParams)
    if (next === 'users') {
      params.delete('tab')
    } else {
      params.set('tab', next)
    }
    setSearchParams(params, { replace: true })
  }

  return (
    <section className="app-section">
      <Tabs value={activeTab} onValueChange={(v) => handleTabChange(v as AdminTabId)}>
        <PageHeader
          title={t('admin.title')}
          right={
            <PageHeaderToolbar>
              <TabsList>
                <TabsTrigger value="users">
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="size-3.5" />
                    <span>{t('admin.tabs.users')}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="roles">
                  <span className="inline-flex items-center gap-1.5">
                    <UserCog className="size-3.5" />
                    <span>{t('admin.tabs.roles')}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="permissions">
                  <span className="inline-flex items-center gap-1.5">
                    <Lock className="size-3.5" />
                    <span>{t('admin.tabs.permissions')}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="all-users">
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="size-3.5" />
                    <span>{t('admin.tabs.allUsers')}</span>
                  </span>
                </TabsTrigger>
              </TabsList>
            </PageHeaderToolbar>
          }
        />
        <TabsContent value="users" className="mt-4 space-y-4">
          <SuperAdminsTab />
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <RolesTab />
        </TabsContent>

        <TabsContent value="permissions" className="mt-4">
          <PermissionsTab />
        </TabsContent>

        <TabsContent value="all-users" className="mt-4 space-y-4">
          <AllUsersTab />
        </TabsContent>
      </Tabs>
    </section>
  )
}
